import { store, getContext } from '@wordpress/interactivity';

import { getVotePercentage } from '../../utils.js';
import { LOCAL_STORAGE_KEY } from '../poll/view-module.js';

const { actions, state, callbacks } = store('mediapress-polls', {
  state: {
    get isCorrect() {
      const { optionId, correctAnswer } = getContext();
      return correctAnswer === optionId;
    },
    get isQuiz() {
      const { correctAnswer } = getContext();
      return !!correctAnswer;
    },
    /**
     * Derived state that returns true if the user has voted for this option.
     */
    get isSelected() {
      const { optionId, selectedOptionId } = getContext();
      return optionId === selectedOptionId;
    },
    /**
     * Derived state that returns the vote percentage for this option.
     */
    get votePercentage() {
      const context = getContext();
      const { count, optionId, showResult } = context;

      if (!count) {
        return 0;
      }

      if (!showResult) {
        return 0;
      }

      return getVotePercentage(count, optionId);
    },
    /**
     * Derived state that returns the vote percentage label for this option.
     */
    get votePercentageLabel() {
      if (state.votePercentage === null) {
        return '';
      }

      return `${state.votePercentage}%`;
    },
    get showPercentage() {
      const { showResult } = getContext();
      return !state.isQuiz && showResult;
    },
    get showCorrect() {
      const { showResult } = getContext();
      return state.isQuiz && showResult && state.isCorrect;
    },
    get showIncorrect() {
      const { showResult } = getContext();
      return state.isQuiz && showResult && !state.isCorrect && state.isSelected;
    },
  },
  actions: {
    /**
     * Handles the keydown event on the poll option.
     *
     * @param {KeyboardEvent} event The keydown event
     *
     * @return {void}
     */
    handleKeydown(event) {
      if (event.key !== 'Enter') {
        return;
      }

      actions.vote();
    },
    /**
     * Handles the click event on the poll option.
     *
     * @param {PointerEvent} event The click event
     *
     * @return {void}
     */
    handleClick(event) {
      // Avoid reacting to synthetic click events, e.g: when using the keyboard to navigate between options.
      if (event.clientX === 0 && event.clientY === 0) {
        return;
      }

      actions.vote();
    },
    /**
     * Handles the vote action.
     *
     * @return {void}
     */
    vote() {
      const context = getContext();
      const { pollId, optionId, showResult } = context;

      if (showResult) {
        return;
      }

      callbacks.vote(pollId, optionId);
    },
  },
  callbacks: {
    /**
     * Cast a vote for a poll option.
     *
     * @param {string} pollId   The poll post id.
     * @param {string} optionId The poll option unique id.
     *
     * @return {void}
     */
    *vote(pollId, optionId) {
      const result = yield fetch(`${state.apiRoot}/poll/${pollId}/vote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          option_id: optionId,
        }),
      })
        .then((res) => res.json())
        .catch((err) => console.error(err.message)); // eslint-disable-line no-console

      if (!result) {
        return;
      }

      const context = getContext();
      const oldTotal = state.totalVotes;
      callbacks.updateLocalStorage(pollId, optionId, oldTotal);

      context.selectedOptionId = optionId;
      callbacks?.calculateCorrectAnswers?.();
      yield callbacks.updatePollCount();
      if (state.totalVotes <= oldTotal) {
        context.count[optionId] = (context.count[optionId] ?? 0) + 1;
      }
      context.showResult = true;
    },

    /**
     * Updates the local storage with the user's vote.
     *
     * @param {string} pollId     The poll post id.
     * @param {string} optionId   The poll option unique id.
     * @param {number} totalVotes The number of votes that existed when we submitted.
     */
    updateLocalStorage: (pollId, optionId, totalVotes) => {
      const existingVotes = callbacks.getLocalVotes();

      const existingVoteIndex = existingVotes.findIndex((vote) => vote.pollId === pollId);

      // User has voted on this poll so update existing value.
      if (existingVoteIndex !== -1) {
        existingVotes[existingVoteIndex] = { pollId, optionId, totalVotes };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(existingVotes));
        return;
      }

      existingVotes.push({ pollId, optionId, totalVotes });
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(existingVotes));
    },
  },
});
